import React, {useState, useEffect, useMemo} from "react";
import {hasAuthParams, useAuth} from "react-oidc-context";
import {db} from "./config/firebase-config";
import useAuthorization from "./auth/useAuthorization";

import {
  collection,
  collectionGroup,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";

import Login from "./components/Login";
import Overview from "./components/Overview";
import Header from "./components/Header";
import Einstellungen from "./components/Einstellungen";
import Protokoll from "./components/Protokoll";
import Changelog from "./components/Changelog";
import Fahrzeug from "./components/Fahrzeug";
import ProtokollFahrzeug from "./components/ProtokollFahrzeug";
import Maengel from "./components/Maengel";
import {ladefehlerText, neueMaengelAnzahl} from "./utils/maengel";

const fahrzeugeRef = collection(db, "fahrzeuge");
const funkkennungenRef = collection(db, "funkkennungen");
const kategorienRef = collection(db, "kategorien");
// Mängel liegen als Subcollection unter dem jeweiligen Fahrzeug — die
// Gesamtsicht entsteht nur über eine Collection-Group-Abfrage. Sie setzt eine
// eigene Security Rule auf dem Gruppenpfad und einen Collection-Group-Index
// voraus; beides pflegt der Betreiber von Hand, siehe docs/firebase-konsole.md.
const maengelRef = collectionGroup(db, "maengel");

const App = () => {
  const auth = useAuth();
  const {hasAppAccess} = useAuthorization();
  const [page, setPage] = useState("overview");
  const [pageKennzeichen, setPageKennzeichen] = useState("testkennzeichen ");

  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [funkkennungen, setFunkkennungen] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [maengel, setMaengel] = useState([]);
  const [maengelLaden, setMaengelLaden] = useState(true);
  const [maengelFehler, setMaengelFehler] = useState("");
  const [searchString, setSearchString] = useState("");
  const [fahrzeugId, setFahrzeugId] = useState("");

  // Der Zähler am Menüpunkt „Mängel" hängt am selben Live-Listener wie die
  // Liste und steht darum hier, nicht in der Mängelseite: er soll auch dann
  // stimmen, wenn diese Seite gar nicht offen ist.
  const neueMaengel = useMemo(() => {
    return neueMaengelAnzahl(maengel, fahrzeuge);
  }, [maengel, fahrzeuge]);

  const changePage = (newPage) => {
    setPage(newPage);
  };

  const gotoFahrzeugPage = (kennzeichen) => {
    setPageKennzeichen(kennzeichen);
    setPage("fahrzeug");
  };

  useEffect(() => {
    const queryFahrzeuge = query(fahrzeugeRef, orderBy("funkkennung"));

    const unsubscribe = onSnapshot(queryFahrzeuge, (snapshot) => {
      let fahrzeugeTmp = [];
      snapshot.forEach((doc) => {
        // console.log("wie oft bin ich hier?");

        fahrzeugeTmp.push({...doc.data(), id: doc.id});
      });
      setFahrzeuge(fahrzeugeTmp);
      // console.log("fahrzeugeTmp (APP)", fahrzeugeTmp);
    });

    const queryFunkkennungen = query(funkkennungenRef, orderBy("bezeichnung"));

    const unsubscribe2 = onSnapshot(queryFunkkennungen, (snapshot) => {
      let funkkennungen = [];
      snapshot.forEach((doc) => {
        funkkennungen.push({...doc.data(), id: doc.id});
      });
      setFunkkennungen(funkkennungen);
    });

    const queryKategorien = query(kategorienRef, orderBy("bezeichnung"));

    const unsubscribe3 = onSnapshot(queryKategorien, (snapshot) => {
      let kategorien = [];
      snapshot.forEach((doc) => {
        kategorien.push({...doc.data(), id: doc.id});
      });
      setKategorien(kategorien);
    });

    const queryMaengel = query(maengelRef, orderBy("datumAngelegt", "desc"));

    const unsubscribe4 = onSnapshot(
      queryMaengel,
      (snapshot) => {
        let maengelTmp = [];
        snapshot.forEach((doc) => {
          // Der Fahrzeugbezug steht nicht im Dokument, sondern nur im Pfad.
          maengelTmp.push({
            ...doc.data(),
            id: doc.id,
            fahrzeugId: doc.ref.parent.parent?.id,
          });
        });
        setMaengel(maengelTmp);
        setMaengelFehler("");
        setMaengelLaden(false);
      },
      (error) => {
        // Sobald dieser Callback existiert, gibt das Firestore-SDK den Fehler
        // nicht mehr selbst aus — und damit auch nicht mehr den Link, mit dem
        // sich ein fehlender Index in der Konsole vorausgefüllt anlegen lässt.
        console.error("Mängel konnten nicht geladen werden", error);
        // Ohne Regel bzw. Index bliebe die Seite sonst wortlos leer.
        setMaengelFehler(ladefehlerText(error));
        setMaengel([]);
        setMaengelLaden(false);
      },
    );

    return () => {
      unsubscribe();
      unsubscribe2();
      unsubscribe3();
      unsubscribe4();
    };
  }, []);

  // return <></>;

  // console.log("fahrzeuge (APP)", fahrzeuge);

  if (
    auth.isLoading ||
    auth.activeNavigator === "signinRedirect" ||
    hasAuthParams()
  ) {
    return <div className="login">Anmeldung wird verarbeitet ...</div>;
  }

  if (!auth.isAuthenticated) {
    return (
      <Login
        fahrzeuge={fahrzeuge}
        kategorien={kategorien}
        funkkennungen={funkkennungen}
        accessDenied={false}
      />
    );
  } else if (!hasAppAccess) {
    return (
      <Login
        fahrzeuge={fahrzeuge}
        kategorien={kategorien}
        funkkennungen={funkkennungen}
        accessDenied={true}
      />
    );
  } else {
    return (
      <>
        <Header
          page={page}
          changePage={changePage}
          searchString={searchString}
          setSearchString={setSearchString}
          neueMaengel={neueMaengel}
        />
        {page === "overview" ? (
          <Overview
            fahrzeuge={fahrzeuge}
            funkkennungen={funkkennungen}
            searchString={searchString}
            setSearchString={setSearchString}
            gotoFahrzeugPage={gotoFahrzeugPage}
          />
        ) : undefined}
        {page === "maengel" ? (
          <Maengel
            maengel={maengel}
            fahrzeuge={fahrzeuge}
            isLoading={maengelLaden}
            error={maengelFehler}
            searchString={searchString}
            gotoFahrzeugPage={gotoFahrzeugPage}
          />
        ) : undefined}
        {page === "einstellungen" ? <Einstellungen /> : undefined}
        {page === "fahrzeug" ? (
          <Fahrzeug
            fahrzeuge={fahrzeuge}
            kategorien={kategorien}
            funkkennungen={funkkennungen}
            pageKennzeichen={pageKennzeichen}
            changePage={changePage}
            setFahrzeugId={setFahrzeugId}
          />
        ) : undefined}
        {page === "protokoll" ? (
          <Protokoll
            fahrzeuge={fahrzeuge}
            funkkennungen={funkkennungen}
            searchString={searchString}
            setSearchString={setSearchString}
          />
        ) : undefined}
        {page === "protokollFahrzeug" ? (
          <ProtokollFahrzeug
            searchString={searchString}
            setSearchString={setSearchString}
            fahrzeugId={fahrzeugId}
            changePage={changePage}
            pageKennzeichen={pageKennzeichen}
          />
        ) : undefined}
        {page === "changelog" ? <Changelog /> : undefined}
      </>
    );
  }
};

export default App;
